// Sage AI advisor analytics events

export type SageAttachmentType = 'image' | 'pdf' | 'document_reference';
export type SageFeedbackRating = 'positive' | 'negative';

// Privacy-preserving message length ranges
function getMessageLengthRange(length?: number): string {
  if (!length || length === 0) return 'empty';
  if (length < 20) return 'very_short';
  if (length < 100) return 'short';
  if (length < 300) return 'medium';
  if (length < 1000) return 'long';
  return 'very_long';
}

export const sageEvents = {
  messageSent: (params: {
    message_length?: number;
    has_attachments: boolean;
    attachment_count: number;
    attachment_types: SageAttachmentType[];
    is_suggested_question: boolean;
    is_followup_question: boolean;
    is_first_message: boolean;
  }) => ({
    event: 'sage_message_sent',
    properties: {
      message_length_range: getMessageLengthRange(params.message_length),
      has_attachments: params.has_attachments,
      attachment_count: params.attachment_count,
      attachment_types: params.attachment_types,
      is_suggested_question: params.is_suggested_question,
      is_followup_question: params.is_followup_question,
      is_first_message: params.is_first_message,
    },
  }),

  messageReceived: (params: {
    response_time_ms: number;
    tool_calls_count: number;
    sources_count: number;
    source_types: string[];
    has_error: boolean;
    input_tokens?: number;
    output_tokens?: number;
  }) => ({
    event: 'sage_message_received',
    properties: {
      response_time_ms: params.response_time_ms,
      tool_calls_count: params.tool_calls_count,
      sources_count: params.sources_count,
      source_types: params.source_types,
      has_error: params.has_error,
      input_tokens: params.input_tokens,
      output_tokens: params.output_tokens,
    },
  }),

  suggestedQuestionClicked: (params: {
    question_index: number;
    question_text_preview: string;
  }) => ({
    event: 'sage_suggested_question_clicked',
    properties: {
      question_index: params.question_index,
      question_text_preview: params.question_text_preview.slice(0, 50),
    },
  }),

  followupClicked: (params: {
    question_text_preview: string;
  }) => ({
    event: 'sage_followup_clicked',
    properties: {
      question_text_preview: params.question_text_preview.slice(0, 50),
    },
  }),

  feedbackSubmitted: (params: {
    rating: SageFeedbackRating;
    has_reasons: boolean;
    reason_count: number;
    reasons: string[];
    has_comment: boolean;
  }) => ({
    event: 'sage_feedback_submitted',
    properties: {
      rating: params.rating,
      has_reasons: params.has_reasons,
      reason_count: params.reason_count,
      reasons: params.reasons,
      has_comment: params.has_comment,
    },
  }),

  citationClicked: (params: {
    source_type: string;
    has_route: boolean;
  }) => ({
    event: 'sage_citation_clicked',
    properties: {
      source_type: params.source_type,
      has_route: params.has_route,
    },
  }),

  conversationStarted: () => ({
    event: 'sage_conversation_started',
    properties: {},
  }),

  conversationLoaded: (params: {
    message_count: number;
  }) => ({
    event: 'sage_conversation_loaded',
    properties: {
      message_count: params.message_count,
    },
  }),

  conversationDeleted: () => ({
    event: 'sage_conversation_deleted',
    properties: {},
  }),

  conversationSearched: (params: {
    query_length: number;
    results_count: number;
  }) => ({
    event: 'sage_conversation_searched',
    properties: {
      query_length: params.query_length,
      results_count: params.results_count,
    },
  }),

  attachmentAdded: (params: {
    attachment_type: SageAttachmentType;
    is_pdf_page: boolean;
    total_attachments: number;
  }) => ({
    event: 'sage_attachment_added',
    properties: {
      attachment_type: params.attachment_type,
      is_pdf_page: params.is_pdf_page,
      total_attachments: params.total_attachments,
    },
  }),

  errorOccurred: (params: {
    error_message_preview: string;
  }) => ({
    event: 'sage_error_occurred',
    properties: {
      error_message_preview: params.error_message_preview.slice(0, 100),
    },
  }),

  retryAttempted: () => ({
    event: 'sage_retry_attempted',
    properties: {},
  }),
};

export type SageEvent = ReturnType<typeof sageEvents[keyof typeof sageEvents]>;
